import * as React from "react";
import { Thermometer } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * PageHeader — section-header für authenticated routes mit org-kontext.
 *
 * Sitzt UNTER dem app-shell-header (der den brand-wordmark + nav trägt) und
 * markiert die page-section (z.B. "Dashboard") mit akzent-icon links und
 * org-name/city rechts. Non-sticky — soll mit content mitscrollen.
 *
 * Mobile (<768px): vertikal stack, akzent oben, org-context darunter.
 */
interface PageHeaderProps {
  title: string;
  orgName?: string;
  orgCity?: string;
  orgPlz?: string;
  /** Optionale action-cluster-children (z.B. button-row rechts, phase B). */
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  orgName,
  orgCity,
  orgPlz,
  actions,
  className,
}: PageHeaderProps): React.ReactElement {
  const hasOrgContext = Boolean(orgName || orgCity);
  return (
    <header
      className={cn(
        "flex flex-col gap-3 pb-4",
        "border-b border-[color:var(--color-border)]",
        "md:flex-row md:items-center md:justify-between md:gap-4",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Thermometer
          aria-hidden="true"
          strokeWidth={2}
          className="size-7 text-[color:var(--color-brand-primary)]"
        />
        <h1
          className={cn(
            "text-2xl font-bold leading-tight",
            "tracking-[-0.02em]",
            "text-[color:var(--color-text-primary)]",
          )}
        >
          {title}
        </h1>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        {hasOrgContext && (
          <div className="flex flex-col text-sm md:items-end">
            {orgName && (
              <p className="font-medium text-[color:var(--color-text-primary)]">{orgName}</p>
            )}
            {orgCity && (
              <p className="text-xs text-[color:var(--color-text-secondary)]">
                {orgPlz && `${orgPlz} `}
                {orgCity}
              </p>
            )}
          </div>
        )}
        {actions && <div className="md:ml-2">{actions}</div>}
      </div>
    </header>
  );
}

PageHeader.displayName = "PageHeader";
